import { useState, useEffect, useMemo } from "react";
import { Building2, Leaf, Users, Shield } from "lucide-react";
import { useWizard } from "@/contexts/WizardContext";
import { MetricCard } from "./common/MetricCard";
import { StoryGeneralCard } from "./sustainability-story/StoryGeneralCard";
import { StoryEnvironmentCard } from "./sustainability-story/StoryEnvironmentCard";
import { StorySocialCard } from "./sustainability-story/StorySocialCard";
import { StoryGovernanceCard } from "./sustainability-story/StoryGovernanceCard";
import { WizardSectionNav } from "@/components/wizard/WizardSectionNav";
import { getSectionByCardKey } from "@shared/section-definitions";
import { calculateCardCompletion } from "@shared/field-definitions";
import { TabCompletionHeader } from "./common/TabCompletionHeader";

export function SustainabilityStoryTab() {
  const [expandedMetric, setExpandedMetric] = useState<string | null>(null);
  const {
    currentSectionId,
    currentSection,
    goToSection,
    isLocked,
    storyGeneralData,
    storyEnvironmentData,
    storySocialData,
    storyGovernanceData,
  } = useWizard();

  // Sync expandedMetric with currentSection
  useEffect(() => {
    if (currentSection && currentSection.tab === "sustainability-story") {
      setExpandedMetric(currentSection.cardKey);
    }
  }, [currentSection]);

  const handleSectionExpand = (cardKey: string) => {
    setExpandedMetric(cardKey);
    const section = getSectionByCardKey("sustainability-story", cardKey);
    if (section) {
      goToSection(section.id);
    }
  };

  const handleSectionClose = () => {
    setExpandedMetric(null);
  };

  // Live completion from state — 0% when empty, increases as fields are filled
  const storyGeneralCompletion = useMemo(
    () => calculateCardCompletion('storyGeneral', storyGeneralData),
    [storyGeneralData]
  );

  const storyEnvironmentCompletion = useMemo(
    () => calculateCardCompletion('storyEnvironment', storyEnvironmentData),
    [storyEnvironmentData]
  );

  const storySocialCompletion = useMemo(
    () => calculateCardCompletion('storySocial', storySocialData),
    [storySocialData]
  );

  const storyGovernanceCompletion = useMemo(
    () => calculateCardCompletion('storyGovernance', storyGovernanceData),
    [storyGovernanceData]
  );

  const sectionCompletions = {
    storyGeneral: storyGeneralCompletion,
    storyEnvironment: storyEnvironmentCompletion,
    storySocial: storySocialCompletion,
    storyGovernance: storyGovernanceCompletion,
  };

  const overallCompletion = Math.round(
    (sectionCompletions.storyGeneral +
      sectionCompletions.storyEnvironment +
      sectionCompletions.storySocial +
      sectionCompletions.storyGovernance) / 4
  );
  const sustainabilityStoryStatusLabel = isLocked
    ? "Finalized"
    : "In progress";

  return (
    <div className="flex gap-6">
      {/* Left Sidebar Navigation */}
      <div className="w-64 flex-shrink-0 hidden lg:block sticky top-[8.25rem] self-start h-[calc(100vh-16.5rem)]">
        <WizardSectionNav
          currentTab="sustainability-story"
          currentSectionId={currentSectionId}
          onSectionClick={(section) => handleSectionExpand(section.cardKey)}
          sectionCompletions={sectionCompletions}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 space-y-4">
        <div>
          <TabCompletionHeader
            title={"Cover page, leadership statement, basis for preparation, practices & strategy"}
            completion={overallCompletion}
            statusLabel={sustainabilityStoryStatusLabel}
          />
        </div>

        {expandedMetric === "storyGeneral" ? (
          <StoryGeneralCard onCollapse={handleSectionClose} />
        ) : expandedMetric === "storyEnvironment" ? (
          <StoryEnvironmentCard onCollapse={handleSectionClose} />
        ) : expandedMetric === "storySocial" ? (
          <StorySocialCard onCollapse={handleSectionClose} />
        ) : expandedMetric === "storyGovernance" ? (
          <StoryGovernanceCard onCollapse={handleSectionClose} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div onClick={() => handleSectionExpand("storyGeneral")} className="cursor-pointer">
              <MetricCard
                icon={Building2}
                title={"General"}
                description={"General sustainability narrative and missing general fields"}
                completion={sectionCompletions.storyGeneral}
                color="text-blue-600"
              />
            </div>
            <div onClick={() => handleSectionExpand("storyEnvironment")} className="cursor-pointer">
              <MetricCard
                icon={Leaf}
                title={"Environment"}
                description={"Environmental sustainability narrative and missing environment fields"}
                completion={sectionCompletions.storyEnvironment}
                color="text-green-600"
              />
            </div>
            <div onClick={() => handleSectionExpand("storySocial")} className="cursor-pointer">
              <MetricCard
                icon={Users}
                title={"Social"}
                description={"Social sustainability narrative and missing social fields"}
                completion={sectionCompletions.storySocial}
                color="text-orange-600"
              />
            </div>
            <div onClick={() => handleSectionExpand("storyGovernance")} className="cursor-pointer">
              <MetricCard
                icon={Shield}
                title={"Governance"}
                description={"Governance sustainability narrative and missing governance fields"}
                completion={sectionCompletions.storyGovernance}
                color="text-purple-600"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
